import { queryOptions } from '@tanstack/react-query'

/**
 * Raportul de seara al unui om, deschis din linkul emailului.
 *
 * NU trece prin `apiGet` / `apiPost`, si asta e chiar miezul: acelea, la 401,
 * cer un token nou si apoi MUTA browserul la ecranul de intrare - iar omul care
 * a apasat linkul din email n-are cont in portal. Ar fi ajuns la "Sign in" in
 * loc de ziua lui, si n-ar fi aflat niciodata de ce. Acelasi drum ca
 * `easeedeskTicketLinks.ts`.
 *
 * Capetele stau sub `/api/v2/public/`, singurul prefix din API scos de sub
 * firewall. Ce le apara e jetonul din adresa, citit pe server.
 *
 * CINE si CARE ZI nu se trimit niciodata de aici - nici in adresa, nici in
 * formular. Serverul le ia din jeton. Daca vreodata cineva adauga `user_id` la
 * vreun `post()` de mai jos, sa stie ca serverul nu-l citeste: singurul lucru
 * care hotaraste a cui e ziua e jetonul.
 */

/** Linkul nu deschide nimic: gresit, retras, expirat, om sters. Un singur fel. */
export class DayReportGone extends Error {
  constructor(message?: string) {
    super(message ?? 'This link is no longer valid.')
    this.name = 'DayReportGone'
  }
}

/** Linkul e bun, dar cererea a fost refuzata - si se spune de ce. */
export class DayReportRefused extends Error {
  readonly reason: string

  constructor(reason: string, message: string) {
    super(message)
    this.name = 'DayReportRefused'
    this.reason = reason
  }
}

const PUBLIC = '/api/v2/public/productivitee/day'

async function call<T>(method: 'GET' | 'POST', path: string, body?: Record<string, unknown>): Promise<T> {
  const init: RequestInit = { method, headers: { Accept: 'application/json' } }

  if (body !== undefined) {
    /*
     * Perechi nume-valoare si atat. Capetele publice citesc numai campuri
     * simple (`PublicDayReportController::fields()` lasa jos orice sub-tablou),
     * deci un obiect trimis de aici n-ar ajunge nicaieri.
     */
    const form = new URLSearchParams()
    for (const [name, value] of Object.entries(body)) {
      if (value === undefined || value === null) continue
      form.append(name, String(value))
    }
    init.body = form
    init.headers = { ...init.headers, 'Content-Type': 'application/x-www-form-urlencoded' }
  }

  const response = await fetch(`${PUBLIC}${path}`, init)

  if (response.status === 410) throw new DayReportGone()

  if (!response.ok) {
    const refused = (await response.json().catch(() => null)) as { error?: string; message?: string } | null
    throw new DayReportRefused(refused?.error ?? 'error', refused?.message ?? 'That could not be saved.')
  }

  return (await response.json()) as T
}

/* ---------------------------------------------------------------- formele */

export interface DayGap {
  /** `HH:MM` */
  start: string
  end: string
  seconds: number
  /** `before` inaintea primei urme a zilei, `after` dupa ultima, `between` intre doua. */
  kind: string
}

export interface DayEntry {
  id: number
  start_time: string
  end_time: string
  kind: number
  kindLabel: string
  note: string
}

export interface DaySchedule {
  enabled: boolean
  /** Ora aleasa de om; `null` = "cand se termina programul meu". */
  sendHour: string | null
  workStart: string | null
  workEnd: string | null
  /** Ora la care CHIAR pleaca mailul, si de unde e luata. */
  effectiveHour: string
  effectiveSource: string
  awayFrom: string | null
  awayTo: string | null
}

export interface DayReportPageData {
  person: { name: string }
  day: string
  weekday: string
  /** Cate secunde mai tine linkul. */
  expiresIn: number
  away: boolean
  measured: {
    start: string
    end: string
    workingSeconds: number
    activeSeconds: number
    inactiveSeconds: number
    activeShare: number
    /** De la prima urma la ultima, minus tacerile: o ora lucrata pe doua masini se numara O DATA. */
    coveredSeconds: number
    machines: string[]
  }
  gaps: DayGap[]
  entries: DayEntry[]
  applications: { name: string; seconds: number }[]
  schedule: DaySchedule
  kinds: { id: number; label: string }[]
  limits: { minGapMinutes: number; maxEntries: number; maxNote: number }
}

/* ---------------------------------------------------------------- capetele */

export function dayReportQuery(token: string) {
  return queryOptions({
    queryKey: ['productivitee', 'day-report', token],
    queryFn: () => call<DayReportPageData>('GET', `/${encodeURIComponent(token)}`),
    retry: false,
  })
}

export interface EntryFields {
  start: string
  end: string
  kind: number
  note: string
}

export function addEntry(token: string, fields: EntryFields) {
  return call<{ ok: true; id: number }>('POST', `/${encodeURIComponent(token)}/entries`, { ...fields })
}

export function updateEntry(token: string, entry: number, fields: EntryFields) {
  return call<{ ok: true; id: number }>('POST', `/${encodeURIComponent(token)}/entries/${entry}`, { ...fields })
}

export function removeEntry(token: string, entry: number) {
  return call<{ ok: true }>('POST', `/${encodeURIComponent(token)}/entries/${entry}/delete`, {})
}

/** Amandoua goale = inapoi la programul implicit. */
export function saveWorkingHours(token: string, hours: { start: string; end: string }) {
  return call<{ ok: true }>('POST', `/${encodeURIComponent(token)}/hours`, { ...hours })
}

export function saveMail(token: string, schedule: { enabled: boolean; hour: string | null }) {
  return call<{ ok: true }>('POST', `/${encodeURIComponent(token)}/mail`, {
    enabled: schedule.enabled ? '1' : '0',
    hour: schedule.hour ?? '',
  })
}

/** O singura perioada, scrisa peste cea de dinainte; amandoua capetele goale o sterg. */
export function saveAway(token: string, period: { from: string; to: string }) {
  return call<{ ok: true }>('POST', `/${encodeURIComponent(token)}/away`, { ...period })
}
